import {
  Model,
  UIField,
  UIIconButton,
  useEffectAsync,
  UIFormatTemplateSelection,
} from "@alphascriptsolutions/client-core";
import { useEffect, useState } from "react";
import { message } from "antd";
import Renderer from "@/components/Renderer";
import { formatTemplatesTablePanelSchema } from "@/utils/sharePricePerformance/panelsSchema";
import { fetchPrimaryTemplateWithImage } from "@/utils/sharePricePerformance/fetchFormatTemplates";
import { getSelectedShapeName } from "@/utils/sharePricePerformance/selectedPlaceHolderLocalStorage";
import { useAlphaAgentStore } from "@/state/alphaAgentStore";
import { usePlaceholderPopupStore } from "@/state/placeholderPopupStore";
import { addFieldsToData, transformGlobalState } from "@/utils/alphaAgent/modelDataHelper";
import { bufferToBase64ImagePrimaryTemplate } from "@/utils/formatTemplate/slides-extraction-handlers";
import { logger } from "@/helpers/tslog";
import FormatTemplateSearchPopup from "./FormatTemplateSearchPopup";
import { 
  getFormatAndPlaceholderValues,
  STORAGE_KEYS 
} from "../../utils/alphaAgent/FormatAndPlaceholderUtils";

export default function FormatAndPlaceholder() {
  const { model, setModel } = useAlphaAgentStore();
  const { fromPlaceholderPopup } = usePlaceholderPopupStore();
  const [templateSearchVisible, setTemplateSearchVisible] = useState(false);
  const [currentPrimaryTemplate, setCurrentPrimaryTemplate] = useState<any>(null);

  useEffectAsync(async () => {
    const path = "/assets/alphaAgent/AlphaAgentFormatTemplate.yml";

    try {
      let loadedModel = await Model.fromFile(path);
      if (!loadedModel.isValid()) {
        throw new Error(`Problem loading model from ${path}: ${loadedModel.getError()}`);
      }

      await addEventsOnModel(loadedModel);
      setModel(loadedModel);
    } catch (err) {
      message.error(err.message);
    }
  }, []);

  const addEventsOnModel = async (model: Model) => {
    model.setSchema(formatTemplatesTablePanelSchema);

    const primaryFormatWithImage = await fetchPrimaryTemplateWithImage();

    const presentationDesign = model.getByName("presentationDesign");
    if (presentationDesign) {
      try {
        // Check if there's a saved template selection
        const savedTemplate = localStorage.getItem("selectedFormatTemplate");
        let templateToShow = primaryFormatWithImage;
        let savedImage = null;

        if (savedTemplate) {
          try {
            const parsedTemplate = JSON.parse(savedTemplate);
            if (parsedTemplate.id && parsedTemplate.formatName) {
              // Use the saved template data for display
              templateToShow = {
                ...primaryFormatWithImage,
                id: parsedTemplate.id,
                formatName: parsedTemplate.formatName,
              };
              savedImage = parsedTemplate.image;
            }
          } catch (parseError) {
            logger.error("Error parsing saved template:", parseError);
          }
        }

        if (templateToShow) {
          if (templateToShow.formatName) {
            (presentationDesign as any).templateName = templateToShow.formatName;
          }
          (presentationDesign as any).value = templateToShow.id;

          if (savedImage) {
            (presentationDesign as any).image = savedImage;
          } else if (templateToShow.frontCoverImage?.buffer) {
            try {
              (presentationDesign as any).image = bufferToBase64ImagePrimaryTemplate(
                templateToShow.frontCoverImage.buffer,
                templateToShow.frontCoverImage.contentType
              );
            } catch (e: any) {
              (presentationDesign as any).image = undefined;
            }
          } else {
            (presentationDesign as any).image = undefined;
          }

          (presentationDesign as any).onClick = () => {
            setTemplateSearchVisible(true);
          };

          setCurrentPrimaryTemplate(templateToShow);
          localStorage.removeItem("selectedFormatTemplate");
        }
      } catch (error) {
        logger.error("Error populating format template selection:", error);
      }
    } else {
    }

    const placeholderSelectionName = model.getByName<UIField>("placeholderSelectionName");
    const selectedPlaceholderName = getSelectedShapeName();
    placeholderSelectionName.value = selectedPlaceholderName ?? "";
    if (selectedPlaceholderName) {
      const store = useAlphaAgentStore.getState();
      store.markAnswered(store.currentStep);
    }

    const placeholderSelectionBtn = model.getByName<UIIconButton>("placeholderSelectionButton");
    if (placeholderSelectionBtn) {
      placeholderSelectionBtn.onClick = () => {
        const rawData = localStorage.getItem(STORAGE_KEYS.ALPHA_CREATE_DATA);
        const globalStateData = JSON.parse(rawData);
        const convertedDataToRequiredForm = transformGlobalState(globalStateData);
        const { model } = useAlphaAgentStore.getState();

        const { title, subTitle, source, notes } = getFormatAndPlaceholderValues();

        const presentationDesign = model.getByName<UIFormatTemplateSelection>("presentationDesign");

        const completeData = addFieldsToData({
          chartObject: convertedDataToRequiredForm,
          customFormatId: presentationDesign.value,
          metadata: {
            chartTitle: {
              enabled: true,
              value: title,
            },
            chartSubTitle: {
              enabled: true,
              value: subTitle,
            },
            source: {
              enabled: true,
              value: source,
            },
            notes: {
              enabled: true,
              value: notes,
            },
          },
          tradingVolume: localStorage.getItem(STORAGE_KEYS.TRADING_VOLUME) === "true",
        });
        localStorage.setItem("ModelDataToBackend", JSON.stringify(completeData));
        
        // Route back to appropriate location
        if (fromPlaceholderPopup) {
          localStorage.setItem("PlaceholderPopupFlag", "true");
        } else {
          localStorage.setItem("AlphaAgentPlaceholder", "true");
        }
        Office.context.ui.messageParent("OPEN_PLACEHOLDER");
      };
    }

  };


  const handleTemplateSelect = async (selectedTemplate: any) => {
    try {
      const presentationDesign = model.getByName("presentationDesign");
      if (presentationDesign && selectedTemplate) {
        if (selectedTemplate.formatName) {
          (presentationDesign as any).templateName = selectedTemplate.formatName;
        }
        (presentationDesign as any).value = selectedTemplate.id;
        if (selectedTemplate.frontCoverImage?.buffer) {
          try {
            const getImageSrc = bufferToBase64ImagePrimaryTemplate(
              selectedTemplate.frontCoverImage.buffer,
              selectedTemplate.frontCoverImage.contentType
            );
            (presentationDesign as any).image = getImageSrc;
          } catch (conversionError) {
            logger.error("Error converting image:", conversionError);
            (presentationDesign as any).image = undefined;
          }
        } else {
          (presentationDesign as any).image = undefined;
        }

        setCurrentPrimaryTemplate(selectedTemplate);
        setModel(model);

        // Store the selected template in localStorage for persistence
        localStorage.setItem(
          "selectedFormatTemplate",
          JSON.stringify({
            id: selectedTemplate.id,
            formatName: selectedTemplate.formatName,
            image: selectedTemplate.frontCoverImage?.buffer
              ? bufferToBase64ImagePrimaryTemplate(
                  selectedTemplate.frontCoverImage.buffer,
                  selectedTemplate.frontCoverImage.contentType
                )
              : undefined,
          })
        );
      }
    } catch (error) {
      logger.error("Error handling template selection:", error);
    }
  };

  if (!model) return null;

  return (
    <div className="mt-3 py-2 px-4">
      <Renderer model={model} />

      {/* Template Search Popup */}
      <FormatTemplateSearchPopup
        visible={templateSearchVisible}
        onClose={() => setTemplateSearchVisible(false)}
        onTemplateSelect={handleTemplateSelect}
        currentPrimaryTemplateId={currentPrimaryTemplate?.id}
      />
    </div>
  );
}
